import React, { useState } from "react";
import { Plus, X, AlertTriangle } from "lucide-react";

const MONTHS = ["Janv", "Févr", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];

export function monthOf(dateStr) {
  return parseInt(dateStr.slice(5, 7), 10) - 1;
}

export const QuotaGauge = ({ used, max, label }) => {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = pct >= 100 ? "#DC2626" : pct >= 80 ? "#B45309" : "#0EA5E9";
  return (
    <div data-testid="quota-gauge">
      <div className="flex items-center justify-between text-[12px] mb-1">
        <span className="text-[#475569]">{label}</span>
        <span className="font-semibold" style={{ color }}>{used} / {max}</span>
      </div>
      <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

function MonthCard({ year, mIdx, target, acts, onAdd, onDelete, disabled }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const [patient, setPatient] = useState("");
  const [busy, setBusy] = useState(false);
  const used = acts.length;
  const over = target > 0 && used > target;
  const daysInMonth = new Date(year, mIdx + 1, 0).getDate();

  const submit = async () => {
    if (!day || !patient.trim()) return;
    setBusy(true);
    const date = `${year}-${String(mIdx + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const ok = await onAdd(date, patient.trim());
    setBusy(false);
    if (ok) { setDay(""); setPatient(""); setOpen(false); }
  };

  return (
    <div className={`rounded-xl border p-3.5 bg-white ${over ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
         data-testid={`quota-month-${mIdx}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-semibold text-[#0F2B5B]">{MONTHS[mIdx]}</span>
        <span className={`text-[11px] font-semibold ${over ? "text-[#DC2626]" : "text-[#475569]"}`}>
          {used}{target > 0 ? ` / ${target}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[22px]">
        {acts.map((a) => (
          <span key={a.id} data-testid={`quota-act-${a.id}`}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#E0F2FE] text-[#0F2B5B] text-[10.5px]">
            {a.date.slice(8)} · {a.patient_ref}
            <button onClick={() => onDelete(a.id)} aria-label="Supprimer"
                    className="grid place-items-center h-3.5 w-3.5 rounded-full hover:bg-[#BAE6FD]">
              <X size={9} />
            </button>
          </span>
        ))}
      </div>
      {open ? (
        <div className="mt-2 flex items-center gap-1.5">
          <select value={day} onChange={(e) => setDay(e.target.value)} data-testid={`quota-day-select-${mIdx}`}
                  className="h-7 rounded border border-[#E2E8F0] bg-white text-[11px] text-[#0F2B5B] px-1">
            <option value="">Jour</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <input value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initiales patient"
                 maxLength={20} data-testid={`quota-patient-input-${mIdx}`}
                 className="flex-1 min-w-0 h-7 rounded border border-[#E2E8F0] px-2 text-[11px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]" />
          <button onClick={submit} disabled={busy || !day || !patient.trim()} data-testid={`quota-act-save-${mIdx}`}
                  className="h-7 px-2 rounded bg-[#0F2B5B] text-white text-[11px] disabled:opacity-40">OK</button>
          <button onClick={() => setOpen(false)} className="h-7 w-7 grid place-items-center rounded text-[#475569] hover:bg-[#F1F5F9]">
            <X size={12} />
          </button>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} disabled={disabled} data-testid={`quota-act-add-${mIdx}`}
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0284C7] hover:text-[#0F2B5B] disabled:opacity-40">
          <Plus size={11} /> Ajouter un acte
        </button>
      )}
    </div>
  );
}

export default function QuotaCalendar({ state, onAdd, onDelete }) {
  const byMonth = Array.from({ length: 12 }, () => []);
  (state.acts || []).forEach((a) => byMonth[monthOf(a.date)].push(a));
  const patients = Object.entries(state.per_patient || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="quota-calendar">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {byMonth.map((acts, i) => (
          <MonthCard key={i} year={state.year} mIdx={i} acts={acts}
                     target={state.monthly_targets?.[i] ?? 0}
                     onAdd={onAdd} onDelete={onDelete}
                     disabled={!state.configured || state.remaining <= 0} />
        ))}
      </div>

      {patients.length > 0 && (
        <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="quota-patients-panel">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#475569] mb-3">
            Compteur par patient — maximum 4 actes / an / patient
          </div>
          <div className="flex flex-wrap gap-2">
            {patients.map(([ref, n]) => (
              <span key={ref} data-testid={`quota-patient-${ref}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] border ${
                      n >= 4 ? "bg-[#FEF2F2] border-[#FCA5A5] text-[#DC2626]"
                             : n === 3 ? "bg-[#FFFBEB] border-[#FCD34D] text-[#B45309]"
                                       : "bg-[#F8FAFC] border-[#E2E8F0] text-[#0F2B5B]"}`}>
              {n >= 4 && <AlertTriangle size={11} />}
              {ref} — {n}/4
            </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
